import Reveal from "@/components/base/Reveal";
import type { Testimonial } from "@/mocks/testimonials";

const FALLBACK_BREAKDOWN = [
  { stars: 5, percent: 94 },
  { stars: 4, percent: 5 },
  { stars: 3, percent: 1 },
  { stars: 2, percent: 0 },
  { stars: 1, percent: 0 },
];

interface ReviewsSummaryProps {
  reviews?: Testimonial[];
}

/**
 * The ratings summary. When real reviews are available it derives the average,
 * count and star distribution from them so the numbers shown to visitors match
 * the AggregateRating markup emitted by the page (a review-snippet requirement).
 * Falls back to the curated figures only while there is nothing loaded yet.
 */
export default function ReviewsSummary({ reviews = [] }: ReviewsSummaryProps) {
  const rated = reviews.filter((review) => Number.isFinite(review.rating) && review.rating > 0);
  const total = rated.length;

  const average = total
    ? rated.reduce((sum, review) => sum + review.rating, 0) / total
    : null;
  const displayAverage = average != null ? average.toFixed(1) : "4.9";

  const breakdown = total
    ? [5, 4, 3, 2, 1].map((stars) => ({
        stars,
        percent: Math.round(
          (rated.filter((review) => Math.round(review.rating) === stars).length / total) * 100,
        ),
      }))
    : FALLBACK_BREAKDOWN;

  const countLabel = total
    ? `Based on ${total.toLocaleString()} verified review${total === 1 ? "" : "s"}`
    : "Based on 1,200+ visits";

  return (
    <section className="sun-glaze bg-background-50 py-16 md:py-20">
      <div className="w-full px-4 md:px-8">
        <Reveal>
          <div className="bg-secondary-50 rounded-lg border border-secondary-200 p-7 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4 flex items-center gap-6">
              <div>
                <p className="font-heading text-5xl md:text-6xl text-foreground-950 font-semibold leading-none">
                  {displayAverage}
                </p>
                <div className="flex items-center gap-1 text-accent-500 mt-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 mt-2">{countLabel}</p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="space-y-2">
                {breakdown.map((row) => (
                  <div key={row.stars} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-foreground-600 w-12 whitespace-nowrap">
                      {row.stars} star
                    </span>
                    <span className="flex-1 h-2 rounded-full bg-background-200 overflow-hidden">
                      <span
                        className="block h-full bg-accent-500"
                        style={{ width: `${row.percent}%` }}
                      ></span>
                    </span>
                    <span className="text-xs font-medium text-foreground-600 w-10 text-right">
                      {row.percent}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-3 grid grid-cols-2 lg:grid-cols-1 gap-5 lg:border-l lg:border-secondary-200 lg:pl-8">
              <div>
                <p className="font-heading text-2xl text-foreground-950 font-semibold">96%</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">Would Recommend</p>
              </div>
              <div>
                <p className="font-heading text-2xl text-foreground-950 font-semibold">150+</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">Groups Hosted</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}